/**
 * purgePageGrids — when a page node is deleted, remove its grid content so the
 * Y.Doc and grid store don't keep orphaned data the (now-gone) page referenced.
 *
 * Clears the page grid's items AND every nested grid under it (sections and
 * the Inlines inside them, at any depth), in both
 * the in-memory grid store (`deleteGrid` cascades nested grids) and the Yjs doc
 * (the `grid:<id>` maps). Yjs maps can't be removed outright — only `.clear()`ed
 * — so empty shells may remain, but they carry no items and are unreferenced.
 */
import type * as Y from "yjs";
import { useGridStore } from "@fredericrous/grid";
import { deleteGridItems } from "@website-builder/y-grid";

import { nestedContainers } from "./inlineRows.js";

export function purgePageGrids(ydoc: Y.Doc, pageGridId: string): void {
  const store = useGridStore.getState();
  // Every depth: a section's grid, and the grid of an Inline inside it.
  const nestedIds = nestedContainers(store.grids, pageGridId).map(
    (c) => c.nestedGridId,
  );
  for (const gid of [pageGridId, ...nestedIds]) {
    // Items live in the flat store (keyed by id, `::parent` = gid); the grid map
    // only carries config/`::bp:`. Purge both so no orphaned bodies remain.
    deleteGridItems(ydoc, gid);
    ydoc.getMap(`grid:${gid}`).clear();
  }
  store.deleteGrid(pageGridId); // cascades section nested grids in the store
}
